import { createLogic } from "redux-logic";
import {
    type StartConfigSaveType,
    type FinishConfigSaveType,
    CONFIGURATION_START_SAVE,
    finishConfigSave,
    errorConfigSave,
    CONFIGURATION_FINISH_SAVE,
} from "../actions/SaveActions";
import Debug from "../../../../../common-deprecated/Debug";
import { getCcSaveExternalConfigurationObject } from "../../utils/typedUtils";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import type { BuildAndBuyLogicType } from "../store";
import { configureCtxConfig } from "../../../../shared-logic/utils/saveUtils";
import type { SavedConfigType } from "../../../../shared-logic/types/SaveTypes";
import { POSTAL_CHANNEL_T1_CARCONFIG, POSTAL_TOPIC_CARCONFIG_END } from "../../../../../common-deprecated/constants";
import { postalPublish } from "../../../../../common-deprecated/utils/postalUtils";
import { getObjectFromT1 } from "../../../../../common-deprecated/utils";

const startConfigSave = (createLogic as BuildAndBuyLogicType<StartConfigSaveType>)({
    type: CONFIGURATION_START_SAVE,

    validate({ getState, action }, allow, reject) {
        const { car, basket, items, save, commonSettings, finance, utils, externalPrice } = getState();
        const { tradeInAemExternalData } = getState();
        // Currently there is no support when quickly changing the configuration while the save service is still saving to
        // keep the last relevant configuration. This might be worth discussing if issues popup.
        if (save.saving) {
            // Only allow config saving when the config isnt saving currently.
            (reject as () => void)();
        } else {
            // Generate configuration object.
            action.config = getCcSaveExternalConfigurationObject(
                commonSettings,
                car,
                basket,
                items,
                tradeInAemExternalData,
                finance.quotes,
                false,
                utils.financeOption,
                externalPrice.co2Specs.value,
                finance.financeProductType,
                false,
                externalPrice.taxLocationId,
            );
            allow(action);
        }
    },

    async process({ getState, action }, dispatch, done) {
        const { config } = action;
        const { bnbSettings, save, navigation, commonSettings } = getState();
        let savingConfig: SavedConfigType;

        try {
            // dxp scripts now init the loadSave object on standalone as well, but it fails due to cors issues
            if (!getObjectFromT1("loadSave") || commonSettings.isStandalone) {
                const fallbackGateway = `${bnbSettings.loadSaveServer}/config/find/json/legacy`;
                const fetchResult = await fetch(fallbackGateway, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(config),
                    mode: "cors",
                });

                savingConfig = await fetchResult.json();
            } else {
                // Inside the T1.loadSave there is already a check to avoid loading the
                // save endpoint twice when providing the same configuration
                savingConfig = await getObjectFromT1("loadSave").get({
                    configuration: JSON.stringify(config),
                });
            }

            // This logic is for handling the CTA's, required for the logic of GFI:
            configureCtxConfig(savingConfig);

            dispatch(finishConfigSave(savingConfig));

            if (navigation.step === NavStep.Summary) {
                // Dispatch carconfig end event
                // This will save your config in a list of "last viewed" configs
                postalPublish({
                    channel: POSTAL_CHANNEL_T1_CARCONFIG,
                    topic: POSTAL_TOPIC_CARCONFIG_END,
                    data: savingConfig,
                });
            }

            if (save.postSaveAction) dispatch(save.postSaveAction);
        } catch (e) {
            Debug.error("Failed to save configuration", e);
            dispatch(errorConfigSave());
        }

        done();
    },
});

const finishConfigSaveLogic = (createLogic as BuildAndBuyLogicType<FinishConfigSaveType>)({
    type: CONFIGURATION_FINISH_SAVE,

    transform({ getState, action }, next) {
        const { data } = action;
        const { bnbSettings } = getState();
        // Fallback if QrCode isn't provided by the response.
        const configCode = data.Personal ? data.Personal.Code : data.ConfigurationCode;
        // data.QrCode is the response from the T1.getObjectFromT1("loadSave") micro service. It is not present on the saved XHR call (legacy in the console). It is added through the micro service.
        // the ${data}qrcode/${configCode} is used when loading a Toyota code, we utilize the direct URL for loading a toyota code (ex.: https://prev.ccsave.toyota-europe.com/config/BybIQxnJV/legacy) therefor the QrCode is not present in the data object
        action.qrCode = data.QrCode || `${bnbSettings.loadSaveServer}/qrcode/${String(configCode)}`;
        next(action);
    },
});

export default [startConfigSave, finishConfigSaveLogic];
export const serverLogic = [finishConfigSaveLogic];
